import { CellContext } from '@tanstack/react-table'
import { useFormContext, useWatch } from 'react-hook-form'

import { Switch } from 'components/Form'
import { useDataForAdUnitBidderSwitch } from 'features/globalSetup'
import { Schema } from 'features/globalSetup/pages/Prebid/Bidders/EditBidderPage/pages/BidParamsAndConnection/form/schema'
import { useExistingWorkspaceBidder } from 'features/globalSetup/pages/Prebid/Bidders/EditBidderPage/pages/BidParamsAndConnection/hooks/useExistingWorkspaceBidder'
import { ParamsKind } from 'features/globalSetup/pages/Prebid/Bidders/types'
import { DomainBidderParameter } from 'features/globalSetup/types/domainBidder'
import { YieldbirdStatus } from 'features/globalSetup/types/yieldbirdStatus'
import { useAbility } from 'providers/casl'
import { AdUnitsForTable } from '../types'
import { useDisableByEmptyRequiredParams } from './useDisableByEmptyRequiredParams'

export type Props = {
  cellContext: CellContext<AdUnitsForTable, unknown>
  kind: ParamsKind
  requiredParams: DomainBidderParameter['name'][]
  workspaceStatus: YieldbirdStatus
}

type SchemaSlice = Pick<Schema, 'adUnitBidders'>

export const EnableBidderToggleCell = ({
  cellContext,
  kind,
  requiredParams,
  workspaceStatus,
}: Props): JSX.Element => {
  const {
    cell: { getValue },
    column: { id: accessorKey },
    row: {
      original: { id: adUnitBidderId },
    },
  } = cellContext

  const {
    workspaceBidder: { accountType },
  } = useExistingWorkspaceBidder()
  const ability = useAbility()

  useDisableByEmptyRequiredParams({
    adUnitBidderId,
    kind,
    requiredParams,
  })

  const bidderPath: string = `adUnitBidders.${adUnitBidderId}`
  const isDisabledPath: string = bidderPath + `.${kind}DisabledByRequiredParams`
  const togglePath: string = bidderPath + `.${accessorKey}`

  const { control } = useFormContext<SchemaSlice>()
  const isDisabledByRequiredParams = useWatch({ exact: true, name: isDisabledPath })

  const isYieldbirdAccount: boolean = accountType === 'yieldbird'

  const { isDisabled, tooltip } = useDataForAdUnitBidderSwitch({
    isDisabledByAuth: ability.cannot(
      'update',
      `GlobalSetupField-prebid-bidders-bid-params-${kind}`,
    ),
    isDisabledByRequiredParams,
    isDisabledByYieldbirdDenied: isYieldbirdAccount && workspaceStatus === 'denied',
    isDisabledByYieldbirdRequested: isYieldbirdAccount && workspaceStatus === 'requested',
    paramsKind: kind,
  })

  return (
    <Switch<SchemaSlice>
      control={control}
      disabled={isDisabled}
      name={togglePath}
      tooltip={tooltip}
      tooltipMode='wrap'
      value={getValue()}
    />
  )
}
